import type { ReactNode } from "react";
import { View, Text, Pressable } from "react-native";
import { Check, Clock, X, ChevronRight } from "lucide-react-native";
import { semanticColors } from "../theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const BROWN = "#6B5548";
const TEXT_LIGHT = "#8A7D74";
const TEXT = "#2A2522";

type DoseStatus = "taken" | "late" | "missed";

const STATUS_COLORS: Record<DoseStatus, string> = semanticColors.doseStatus;

const STATUS_ICONS: Record<DoseStatus, typeof Check> = {
  taken: Check,
  late: Clock,
  missed: X,
};

type ListRowProps = {
  /** Left-side icon or image element */
  icon?: ReactNode;
  /** Primary label */
  title: string;
  /** Inline badges rendered next to the title */
  titleRight?: ReactNode;
  /** Secondary line below the title */
  subtitle?: string;
  /** Right-side content (date label, value+unit, etc.) rendered before the chevron */
  right?: ReactNode;
  /** Called when the row body is pressed */
  onPress?: () => void;
  /** Show a circular checkbox */
  trackable?: boolean;
  done?: boolean;
  onToggle?: () => void;
  /** Override title color (e.g. for urgent items) */
  titleColor?: string;
  /** Override subtitle color */
  subtitleColor?: string;
  /** Render the title with a strike-through (e.g. stopped medications) */
  titleStrikethrough?: boolean;
  /** Status of the logged dose — drives checkbox color */
  doneStatus?: DoseStatus;
  /**
   * Stable analytics identifier. The row's title travels as the event's
   * `label`; this is the half a funnel groups on.
   *
   * Required, not defaulted: the checkbox interpolates it
   * (`${action}_checkbox`), and an absent value would reach PostHog as the
   * literal `"undefined_checkbox"` — a bucket that looks real and groups
   * unrelated rows together.
   */
  action: string;
};

/**
 * @deprecated Prefer `Row` plus a thin domain wrapper. This remains for
 * backwards compatibility with older surfaces.
 */
export default function ListRow({
  icon,
  title,
  titleRight,
  subtitle,
  right,
  onPress,
  trackable = false,
  done = false,
  onToggle,
  titleColor,
  subtitleColor,
  titleStrikethrough = false,
  doneStatus,
  action,
}: ListRowProps) {
  const capturePress = usePressCapture("ListRow");
  return (
    <View
      className="flex-row items-center"
      style={{
        gap: 10,
        paddingHorizontal: 16,
        paddingVertical: 12,
        opacity: done ? 0.7 : 1,
      }}
    >
      {trackable &&
        (() => {
          const checkColor =
            done && doneStatus ? STATUS_COLORS[doneStatus] : BROWN;
          const CheckIcon =
            done && doneStatus ? STATUS_ICONS[doneStatus] : Check;
          return (
            <Pressable
              onPress={() => {
                capturePress(pressLabel(title), `${action}_checkbox`);
                onToggle?.();
              }}
              hitSlop={8}
            >
              <View
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 12,
                  backgroundColor: done ? checkColor : "transparent",
                  borderWidth: done ? 0 : 1.5,
                  borderColor: done ? checkColor : "rgba(0,0,0,0.12)",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {done && <CheckIcon size={14} color="#fff" strokeWidth={2.5} />}
              </View>
            </Pressable>
          );
        })()}

      <Pressable
        onPress={() => {
          capturePress(pressLabel(title), action);
          onPress?.();
        }}
        disabled={!onPress}
        className="flex-1 flex-row items-center"
        style={{
          gap: 10,
        }}
      >
        {icon && (
          <View
            style={{
              width: 28,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </View>
        )}

        <View style={{ flex: 1 }}>
          <View className="flex-row items-center" style={{ gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                fontWeight: "600",
                color: titleColor ?? TEXT,
                textDecorationLine: titleStrikethrough
                  ? "line-through"
                  : "none",
              }}
            >
              {title}
            </Text>
            {titleRight}
          </View>
          {subtitle ? (
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: subtitleColor ?? TEXT_LIGHT,
                marginTop: 2,
              }}
            >
              {subtitle}
            </Text>
          ) : null}
        </View>

        {right}

        {onPress && (
          <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.5} />
        )}
      </Pressable>
    </View>
  );
}
